import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Cookie, X, Settings } from 'lucide-react';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      date: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      date: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleCustomSettings = () => {
    setShowSettings(!showSettings);
  };

  const handleSaveSettings = (settings) => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      ...settings,
      necessary: true, // Always required
      date: new Date().toISOString()
    }));
    setIsVisible(false);
    setShowSettings(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {!showSettings ? (
          <div className="py-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Cookie className="w-6 h-6 text-green-600 mt-1" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Utilizamos cookies para mejorar tu experiencia
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    Utilizamos cookies propias y de terceros para analizar el uso del sitio web y mostrarte publicidad relacionada con tus preferencias. 
                    Puedes aceptar todas las cookies, configurarlas o rechazar su uso. 
                    <a href="#" className="text-green-600 hover:text-green-700 underline ml-1">
                      Más información en nuestra política de cookies
                    </a>
                  </p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                <Button
                  onClick={handleCustomSettings}
                  variant="outline"
                  size="sm"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Configurar
                </Button>
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  size="sm"
                  className="border-gray-300 text-gray-700 hover:bg-gray-50"
                >
                  Rechazar
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  size="sm"
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  Aceptar todas
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <CookieSettings onSave={handleSaveSettings} onClose={() => setShowSettings(false)} />
        )}
      </div>
    </div>
  );
};

const CookieSettings = ({ onSave, onClose }) => {
  const [settings, setSettings] = useState({
    analytics: true,
    marketing: false
  });

  const handleSave = () => {
    onSave(settings);
  };

  return (
    <div className="py-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          Configuración de cookies
        </h3>
        <Button
          onClick={onClose}
          variant="ghost"
          size="sm"
          className="text-gray-500 hover:text-gray-700"
        >
          <X className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-6">
        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
          <div>
            <h4 className="font-medium text-gray-900">Cookies necesarias</h4>
            <p className="text-sm text-gray-600">
              Esenciales para el funcionamiento básico del sitio web
            </p>
          </div>
          <div className="text-green-600 font-medium">Siempre activas</div>
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
          <div>
            <h4 className="font-medium text-gray-900">Cookies de análisis</h4>
            <p className="text-sm text-gray-600">
              Nos ayudan a entender cómo interactúas con nuestro sitio web
            </p>
          </div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.analytics}
              onChange={(e) => setSettings({...settings, analytics: e.target.checked})}
              className="sr-only"
            />
            <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${
              settings.analytics ? 'bg-green-600' : 'bg-gray-300'
            }`}>
              <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 mt-1 ml-1 ${
                settings.analytics ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </div>
          </label>
        </div>

        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
          <div>
            <h4 className="font-medium text-gray-900">Cookies de marketing</h4>
            <p className="text-sm text-gray-600">
              Para mostrarte anuncios personalizados y medir su efectividad
            </p>
          </div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={settings.marketing}
              onChange={(e) => setSettings({...settings, marketing: e.target.checked})}
              className="sr-only"
            />
            <div className={`w-11 h-6 rounded-full transition-colors duration-200 ${
              settings.marketing ? 'bg-green-600' : 'bg-gray-300'
            }`}>
              <div className={`w-4 h-4 bg-white rounded-full shadow-md transform transition-transform duration-200 mt-1 ml-1 ${
                settings.marketing ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </div>
          </label>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <Button
          onClick={() => onSave({ analytics: false, marketing: false })}
          variant="outline"
          className="border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Rechazar todo
        </Button>
        <Button
          onClick={() => onSave({ analytics: true, marketing: true })}
          variant="outline"
          className="border-gray-300 text-gray-700 hover:bg-gray-50"
        >
          Aceptar todo
        </Button>
        <Button
          onClick={handleSave}
          className="bg-green-600 hover:bg-green-700 text-white"
        >
          Guardar configuración
        </Button>
      </div>
    </div>
  );
};

export default CookieBanner;